/**
 * ticket-type-description — one ticket type read whole, mounted by `show()`
 * from either of `ticket-type-row`'s two doors, the name or the icon beside
 * it: the two facts a purchase is made of at reading scale, the
 * type's own description in full under them, and a footer that keeps the
 * purchase reachable while the buyer reads.
 *
 * A named element and not an inline `show(() => html\`<div>…\`)`: an
 * anonymous root reports as the tag `div`, which names nothing, collides with
 * every other anonymous overlay on the page, and cannot anchor a live
 * assertion.
 *
 * It takes the already-resolved `TicketTypeRow` the row itself renders and
 * nothing else. Price formatting, purchasability, the localized copy of every
 * string on screen — each is a different checkout's own business rule, so this
 * component resolves none of them, exactly as `ticket-type-row` resolves none
 * of them. Sharing that one type rather than growing a parallel set of scalar
 * props is what makes the overlay and the row two readings of one fact:
 * whatever the caller decided for the row is what the overlay shows, with no
 * second resolution to drift from it.
 *
 * Name and price go up two rungs from the row's title/md to headline/sm — the
 * lowest headline rung, and the same family the event's own name takes in the
 * header — because the overlay is read at reading distance rather than at list
 * density, and a heading over a paragraph is what that reads as where a
 * magnified list row would not. They share the rung for the reason the row
 * gives for stacking them a rung apart: they are the two facts a purchase is
 * made of. The name is free to wrap here; only the row's own fixed height has
 * a reason to cut it.
 *
 * The body reads at `body/lg` on `leading-relaxed` — the rung and the leading
 * `event-description` gives the event's own description on this same page, for the
 * same reason it gives them: this overlay exists because the reader chose to
 * read, which is where sustained reading's size starts rather than a
 * caption's. It carries no bound of any kind — no clamp, no fade, no ellipsis.
 * This is the only place in the product the operator's own text is rendered at
 * all, so a trim here would leave it unreachable anywhere; the row prints none
 * of it, which is exactly what buys this surface the room to print all of it.
 * So every newline stays a hard break inside its
 * paragraph and every blank line becomes a real 16px interval:
 * `whitespace-pre-line` alone renders a blank line as exactly one empty line
 * box, which measures the same as the pitch inside a paragraph at every
 * leading, so a paragraph the operator separated arrives looking like a line
 * they wrapped.
 *
 * The footer is `sticky bottom-0` inside the overlay's OWN scroll region, never
 * against the window: the overlay's mount is the scrollport, so a footer
 * declared here rides it and never floats over a phone frame drawn on a
 * desktop. Back sits left as a text button and the stepper right, and the two
 * emphases are not interchangeable: the row underneath increments on its whole
 * body, so its own `+` is one of two ways to buy and is drawn as the quieter
 * one, while here the `+` is the only one there is — a bordered Back beside it
 * would draw the eye of a buyer who came to decide toward the one gesture that
 * decides nothing. The space between them is empty on purpose — the two gestures are opposites and a gap is what stops a
 * thumb reaching for one from landing on the other. The stepper renders only
 * for a slot that HAS one: a sold-out or locked type still opens its
 * description, and the description is the whole reason the overlay was tapped.
 *
 * Increment and decrement leave as this element's own CustomEvents rather than
 * being written anywhere: `show()` mounts this detached from the row, so
 * nothing here can reach the caller's qty write on its own, and the row that
 * opened it relays them through the same `increment`/`decrement` it already
 * dispatches — one qty write for both surfaces, and the caller wires nothing
 * new. Back leaves as the platform `close` event the overlay already resolves
 * its own subscription with.
 *
 * Content-only, per schmancy's overlay contract: no surface of its own at the
 * root, no full-bleed fill, no `:host { display: block }` — the overlay
 * supplies the material and blockifies the mount — so it pads itself and
 * nothing else. Dismissal, focus trap, scroll lock, sibling inert, Escape,
 * back-button and focus restore all come from the overlay, which is why the
 * only button that closes anything here is the Back the footer names.
 *
 * Untruncated at any length: `TicketType.TicketTypeSchema` caps nothing, so the
 * overlay scrolls rather than trims. It declares no height cap and owns no
 * scroll region — the overlay's own mount is already `overflow-auto` under a
 * surface capped at 90% of whatever is the viewport there, the window or the
 * frame a `[data-fill-boundary]` declares. A `max-h-[80vh]` here would measure
 * the real window instead, so inside a 375px phone frame drawn on a desktop it
 * resolves larger than the frame and the sheet spills out of the phone it is
 * supposed to be inside.
 *
 * Width is the same argument one axis over, and it is declared on the HOST
 * rather than on the root, because the host is the box whose measure the
 * overlay actually reads: the anchored mount is a grid whose column is sized
 * from its one item's own max-content contribution, so a `max-width: 100%`
 * anywhere INSIDE this element resolves against a track this element itself
 * widened and clamps nothing. A bare `w-md` on the root therefore hands the
 * mount a 448px track inside a surface the overlay already capped at the
 * viewport less its 16px inset, and the two facts pinned to the right edge —
 * the price and the `+` — land off-screen on a phone, unreachable rather than
 * merely clipped.
 *
 * So the host declares `min(28rem, 100dvw - 2rem)`: 28rem is the measure this
 * content is composed for, and the second term is the anchored surface's own
 * cap restated, so the track can never exceed the box the overlay laid out.
 * `max-width: 100%` still rides beside it for the bounded case — a
 * `[data-fill-boundary]` frame renders as a sheet whose mount IS definite, and
 * there the percentage resolves and clamps to the frame, which is the one
 * place `dvw` would over-measure by reading the real desktop window. The pair
 * covers both layouts with one declaration each.
 *
 * It is static CSS and not a `w-[min(28rem,calc(100dvw-2rem))]` utility for two
 * reasons that both land on the same result: Tailwind 4's extractor does not
 * reliably emit an arbitrary value nesting parentheses that deep, and a class
 * this repo invents is generated into schmancy's baked shadow-DOM sheet only if
 * schmancy's own `@source inline(...)` names it — so the utility would compile
 * to nothing and the width would silently be `auto`.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { css, html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import type { TicketTypeRow } from "./ticket-type-row.view.ts";

@schmancyElement("ticket-type-description")
export class TicketTypeDescriptionView extends SchmancyElement {
	static styles = [
		css`
			:host {
				width: min(28rem, 100dvw - 2rem);
				max-width: 100%;
			}
		`,
	];

	/** The same already-resolved row the list rendered — the overlay's whole
	 *  input, pushed again by the row on every caller render so the numeral in
	 *  the footer is the basket's own and never a snapshot taken at open. */
	@property({ type: Object }) row!: TicketTypeRow;

	override render(): TemplateResult {
		const r = this.row;
		// Narrowed before the template rather than inside it: `when`'s callback
		// carries whatever the condition narrowed, and the slot's other three
		// kinds have no quantity to step.
		const stepper = r.slot.kind === "stepper" ? r.slot : null;

		return html`
			<div class="flex flex-col">
				<div class="flex flex-col gap-6 p-6">
					<div class="flex items-baseline gap-3">
						<schmancy-typography type="headline" token="sm" class="min-w-0"
							>${r.name}</schmancy-typography
						>
						<schmancy-typography
							type="headline"
							token="sm"
							class="ml-auto shrink-0 tabular-nums"
							>${r.priceLabel}</schmancy-typography
						>
					</div>
					<div class="grid gap-4">
						${repeat(
							(r.description ?? "").split(/\n{2,}/),
							(_, i) => i,
							(paragraph) => html`
								<schmancy-typography
									type="body"
									token="lg"
									class="whitespace-pre-line leading-relaxed"
									>${paragraph}</schmancy-typography
								>
							`,
						)}
					</div>
				</div>

				<schmancy-surface
					opacity="translucent"
					elevated
					class="sticky bottom-0 flex items-center gap-2 px-6 py-4"
				>
					<schmancy-button
						variant="text"
						@click=${() => {
							this.dispatchEvent(
								new CustomEvent("close", { bubbles: true, composed: true }),
							);
						}}
						>${r.descriptionBackLabel}</schmancy-button
					>
					<div class="flex-1"></div>
					${when(
						stepper,
						(s) => html`
							<schmancy-icon-button
								icon="remove"
								size="xs"
								variant="outlined"
								color="neutral"
								class=${this.classMap({ invisible: s.qty === 0 })}
								?disabled=${!s.canDecrement}
								aria-label=${s.decrementAriaLabel}
								@click=${() => {
									this.dispatchEvent(
										new CustomEvent("decrement", {
											bubbles: true,
											composed: true,
										}),
									);
								}}
							></schmancy-icon-button>
							<schmancy-typography
								type="headline"
								token="lg"
								class=${this.classMap({
									"w-12 text-center tabular-nums": true,
									"text-primary-default": r.selected,
								})}
								>${s.qty}</schmancy-typography
							>
							<schmancy-icon-button
								icon="add"
								size="sm"
								variant="outlined"
								color="neutral"
								?disabled=${!s.canIncrement}
								aria-label=${s.incrementAriaLabel}
								@click=${() => {
									this.dispatchEvent(
										new CustomEvent("increment", {
											bubbles: true,
											composed: true,
										}),
									);
								}}
							></schmancy-icon-button>
						`,
					)}
				</schmancy-surface>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"ticket-type-description": TicketTypeDescriptionView;
	}
}
